import React, { FC, ReactNode } from 'react';
import styled from '@emotion/styled';
import config from '@app/config';
import { findShownSectionLabel, SectionCategoryNames } from '@app/domain/sections';
import LinkSelectableBulletedItem from '@app/components/LinkSelectableBulletedItem';

interface Props {
  category: SectionCategory | undefined;
  sections: Section[];
  selectedSectionId: string | undefined;
  className?: string;
}

const SectionCategory: FC<Props> = ({ category, sections, selectedSectionId, className }) => {
  return (
    <div className={className}>
      <SectionTitle>{category && SectionCategoryNames[category]}</SectionTitle>
      <NavItems>
        {sections.map((section): ReactNode => (
          <LinkSelectableBulletedItem
            id={`btn-sidebar-item-${category}-${section.id}`}
            dataTestId={`btn-openselect-item-${section.id}`}
            key={section.id}
            selected={section.id === selectedSectionId}
            to={section.baseRoute.path}
          >
            <SectionEntryName section={section} />
          </LinkSelectableBulletedItem>
        ))}
      </NavItems>
    </div>
  );
};

export default SectionCategory;

const SectionEntryName: FC<{ section: Section }> = ({ section }) => {
  const label = findShownSectionLabel(config.sectionLabels, section.baseRoute.path, Date.now());
  return (
    <EntryName>
      <span>{section.name}</span>
      {label && <EntryLabel>{label.text}</EntryLabel>}
    </EntryName>
  );
};

const SectionTitle = styled.h3`
  font-family: 'SF Pro', 'Inter', system-ui, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #ffffff52;
  padding-left: 0.75rem;
  margin: 0 0 0.5rem 0;
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const EntryName = styled.span`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  & > span:first-of-type {
    flex: 1;
  }
`;

// Pulled into the entry's padding, so a labelled entry stays as tall as the rest.
const EntryLabel = styled.span`
  margin: -3px 0;
  padding: 2px 6px;
  border-radius: 9999px;
  background-color: #0090ff;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
`;
